package ai.jessie.web.projects

import ai.jessie.shared.projects.ProjectActivityResponse
import ai.jessie.shared.projects.ProjectProblemResponse
import ai.jessie.shared.projects.ProjectResponse
import ai.jessie.shared.projects.ProjectSelectionsResponse
import ai.jessie.shared.projects.ProjectStateRequest
import ai.jessie.shared.projects.SaveProjectRequest
import ai.jessie.web.onboarding.apiRequest
import ai.jessie.web.onboarding.decodeProblem
import ai.jessie.web.onboarding.validOrganisationId
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.serialization.json.Json

// UC-010. A null project ID addresses the collection: saving creates a project.
interface ProjectPort {
    suspend fun list(organisationId: String): List<ProjectResponse>

    suspend fun selections(organisationId: String): ProjectSelectionsResponse

    suspend fun read(organisationId: String, projectId: String): ProjectResponse

    suspend fun save(
        organisationId: String,
        projectId: String?,
        request: SaveProjectRequest,
    ): ProjectResponse

    // A4, A8 and A9. The request carries the last project `updatedAt`, also for a position.
    suspend fun change(organisationId: String, change: ProjectChange, request: ProjectStateRequest)

    suspend fun activity(organisationId: String, projectId: String): ProjectActivityResponse
}

enum class ProjectOperation(internal val path: String) {
    DEACTIVATE("deactivate"),
    REMOVE("remove"),
}

// A null position ID targets the project itself.
data class ProjectChange(
    val operation: ProjectOperation,
    val projectId: String,
    val positionId: String? = null,
)

val LocalProjectPort =
    staticCompositionLocalOf<ProjectPort> {
        error("Project port must be supplied by the application")
    }

class ProjectRequestFailure(val status: Int, val detail: ProjectProblemResponse) :
    Exception("Project request failed")

class BrowserProjectPort : ProjectPort {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun list(organisationId: String) =
        json.decodeFromString<List<ProjectResponse>>(request(organisationId, ""))

    override suspend fun selections(organisationId: String) =
        json.decodeFromString<ProjectSelectionsResponse>(request(organisationId, "/selections"))

    override suspend fun read(organisationId: String, projectId: String) =
        json.decodeFromString<ProjectResponse>(request(organisationId, child(projectId)))

    override suspend fun save(
        organisationId: String,
        projectId: String?,
        request: SaveProjectRequest,
    ) =
        json.decodeFromString<ProjectResponse>(
            request(organisationId, child(projectId), json.encodeToString(request))
        )

    override suspend fun change(
        organisationId: String,
        change: ProjectChange,
        request: ProjectStateRequest,
    ) {
        val position = change.positionId?.let { "/positions${child(it)}" }.orEmpty()
        val path = "${child(change.projectId)}$position/${change.operation.path}"
        try {
            request(organisationId, path, json.encodeToString(request))
        } catch (failure: ProjectRequestFailure) {
            // The shared transport accepts only 200; state changes succeed without content.
            if (failure.status != HTTP_NO_CONTENT) throw failure
        }
    }

    override suspend fun activity(organisationId: String, projectId: String) =
        json.decodeFromString<ProjectActivityResponse>(
            request(organisationId, "${child(projectId)}/activity")
        )

    private fun child(id: String?): String {
        if (id == null) return ""
        require(validProjectId(id))
        return "/$id"
    }

    private suspend fun request(
        organisationId: String,
        path: String,
        body: String? = null,
    ): String {
        require(validOrganisationId(organisationId))
        return apiRequest(json, "/organisations/$organisationId/projects$path", body) { status, text
            ->
            val detail = json.decodeProblem(text, ProjectProblemResponse(""))
            ProjectRequestFailure(status, detail)
        }
    }
}

// Project and position public IDs share the organisation ID format.
fun validProjectId(value: String) = validOrganisationId(value)

private const val HTTP_NO_CONTENT = 204
